import React, { useMemo, useState } from "react";
import {
  FiArrowDown,
  FiArrowUp,
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiExternalLink,
  FiFileText,
  FiFolder,
  FiLink,
  FiMoreVertical,
  FiPlus,
  FiStar,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
} from "react-icons/fi";

import {
  getLevelLabel,
  getMenuItemHref,
  getMenuItemType,
} from "../utils/menuAdminUtils";

export default function EditableTree({
  tree = [],
  onCreateFolder,
  onCreatePage,
  onCreateUrl,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
}) {
  if (!tree.length) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-slate-400 dark:bg-slate-800">
          <FiFolder className="text-2xl" />
        </div>

        <h3 className="mt-4 text-lg font-bold">Aucun élément</h3>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Créez un premier onglet pour commencer l’organisation du menu.
        </p>

        <button
          type="button"
          onClick={() => onCreateFolder(null)}
          className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-sky-700"
        >
          <FiPlus />
          Créer un onglet
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <NodeList
        parentId={null}
        nodes={tree}
        depth={0}
        onCreateFolder={onCreateFolder}
        onCreatePage={onCreatePage}
        onCreateUrl={onCreateUrl}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggle={onToggle}
        onReorder={onReorder}
      />
    </div>
  );
}

function NodeList({
  parentId = null,
  nodes = [],
  depth = 0,
  onCreateFolder,
  onCreatePage,
  onCreateUrl,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
}) {
  const move = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= nodes.length) return;

    const orderedIds = nodes.map((node) => node.id);
    const [moved] = orderedIds.splice(fromIndex, 1);

    orderedIds.splice(toIndex, 0, moved);

    onReorder(parentId, orderedIds);
  };

  return (
    <ul className="space-y-3">
      {nodes.map((node, index) => (
        <NodeItem
          key={node.id}
          node={node}
          index={index}
          count={nodes.length}
          depth={depth}
          onMoveUp={() => move(index, index - 1)}
          onMoveDown={() => move(index, index + 1)}
          onCreateFolder={onCreateFolder}
          onCreatePage={onCreatePage}
          onCreateUrl={onCreateUrl}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggle={onToggle}
          onReorder={onReorder}
        />
      ))}
    </ul>
  );
}

function NodeItem({
  node,
  index,
  count,
  depth,
  onMoveUp,
  onMoveDown,
  onCreateFolder,
  onCreatePage,
  onCreateUrl,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
}) {
  const [open, setOpen] = useState(true);
  const [actionsOpen, setActionsOpen] = useState(false);

  const children = Array.isArray(node.children) ? node.children : [];
  const hasChildren = children.length > 0;
  const type = getMenuItemType(node);
  const href = getMenuItemHref(node);

  const leftPadding = useMemo(() => {
    if (depth <= 0) return "pl-4";
    if (depth === 1) return "pl-8";
    if (depth === 2) return "pl-12";

    return "pl-16";
  }, [depth]);

  return (
    <li className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div
        className={`flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between ${leftPadding}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-3">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {open ? <FiChevronDown /> : <FiChevronRight />}
              </button>
            ) : (
              <span className="h-8 w-8 shrink-0" />
            )}

            <TypeIcon type={type} depth={depth} />

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-slate-950 dark:text-slate-50">
                  {node.label}
                </span>

                <TypeBadge type={type} depth={depth} />

                <ActiveBadge active={!!node.is_active} />

                {node.is_favorite ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                    <FiStar />
                    Raccourci
                  </span>
                ) : null}
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                {href ? (
                  <span className="inline-flex items-center gap-1">
                    {type === "page" ? <FiFileText /> : <FiExternalLink />}
                    {href}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <FiFolder />
                    Contient des éléments
                  </span>
                )}

                {hasChildren ? (
                  <span>
                    — {children.length} sous-élément
                    {children.length > 1 ? "s" : ""}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 md:justify-end">
          <IconButton
            title="Monter"
            onClick={onMoveUp}
            disabled={index === 0}
          >
            <FiArrowUp />
          </IconButton>

          <IconButton
            title="Descendre"
            onClick={onMoveDown}
            disabled={index === count - 1}
          >
            <FiArrowDown />
          </IconButton>

          <div className="relative">
            <button
              type="button"
              onClick={() => setActionsOpen((current) => !current)}
              className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-sky-700"
            >
              <FiPlus />
              Ajouter
            </button>

            {actionsOpen ? (
              <div className="absolute right-0 top-full z-20 mt-2 w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900">
                <ActionMenuButton
                  icon={<FiFolder />}
                  label="Ajouter un dossier ici"
                  onClick={() => {
                    setActionsOpen(false);
                    onCreateFolder(node.id);
                  }}
                />

                <ActionMenuButton
                  icon={<FiFileText />}
                  label="Ajouter une page ici"
                  onClick={() => {
                    setActionsOpen(false);
                    onCreatePage(node.id);
                  }}
                />

                <ActionMenuButton
                  icon={<FiLink />}
                  label="Ajouter un lien ici"
                  onClick={() => {
                    setActionsOpen(false);
                    onCreateUrl(node.id);
                  }}
                />
              </div>
            ) : null}
          </div>

          <IconButton title="Modifier" onClick={() => onEdit(node)}>
            <FiEdit2 />
          </IconButton>

          <IconButton
            title={node.is_active ? "Désactiver" : "Activer"}
            onClick={() => onToggle(node.id)}
          >
            {node.is_active ? <FiToggleLeft /> : <FiToggleRight />}
          </IconButton>

          <button
            type="button"
            title="Supprimer"
            onClick={() => onDelete(node)}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-xl bg-rose-50 px-3 text-xs font-bold text-rose-700 transition hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-950/70"
          >
            <FiTrash2 />
          </button>
        </div>
      </div>

      {hasChildren && open ? (
        <div className="border-t border-slate-100 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/40">
          <NodeList
            parentId={node.id}
            nodes={children}
            depth={depth + 1}
            onCreateFolder={onCreateFolder}
            onCreatePage={onCreatePage}
            onCreateUrl={onCreateUrl}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggle={onToggle}
            onReorder={onReorder}
          />
        </div>
      ) : null}
    </li>
  );
}

function TypeIcon({ type, depth }) {
  const className =
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl";

  if (depth === 0) {
    return (
      <div className={`${className} bg-sky-50 text-sky-700 dark:bg-sky-950/30 dark:text-sky-200`}>
        <FiFolder />
      </div>
    );
  }

  if (type === "folder") {
    return (
      <div className={`${className} bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-200`}>
        <FiFolder />
      </div>
    );
  }

  if (type === "page") {
    return (
      <div className={`${className} bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200`}>
        <FiFileText />
      </div>
    );
  }

  return (
    <div className={`${className} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200`}>
      <FiLink />
    </div>
  );
}

function TypeBadge({ type, depth }) {
  const label = getLevelLabel(depth, type);

  const cls =
    type === "folder"
      ? depth === 0
        ? "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200"
        : "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/50 dark:bg-violet-950/30 dark:text-violet-200"
      : type === "page"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200"
      : "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200";

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${cls}`}
    >
      {label}
    </span>
  );
}

function ActiveBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${
        active
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200"
          : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200"
      }`}
    >
      {active ? <FiToggleRight /> : <FiToggleLeft />}
      {active ? "Actif" : "Inactif"}
    </span>
  );
}

function IconButton({ children, title, onClick, disabled = false }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
    >
      {children}
    </button>
  );
}

function ActionMenuButton({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
    >
      {icon}
      {label}
    </button>
  );
}